import { ApolloLink, Observable } from 'apollo-link';
import readableStreamToJSON from './stream';
import { getAuthenticationError } from '../server/server-client-helpers';
import { navigateTo, POST_TYPES } from './helpers/navigation';

/**
 * This link runs first in the chain of links which handle incoming
 * API responses. The aim of this is to hang any responses which have
 * triggered a 401 unauthorized response due to an expired token. In
 * this scenario, the user is redirected back to /sites to have a new
 * token assigned.
 */
const sessionExpiryLink = new ApolloLink((operation, forward) => new Observable(observer => {
  const subscription = forward(operation).subscribe({
    next: result => {
      // If all is fine, just pass this response to the next link
      observer.next(result);
    },
    error: async networkError => {
      if (
          networkError?.response &&
          networkError?.response?.clone &&
          networkError?.statusCode === 401
      ) {
        try {
          const { body } = networkError.response.clone();
          const parsedBody = await readableStreamToJSON(body);
          const authenticationError = getAuthenticationError(parsedBody.error);
          // If we get back an authentication error, then we need to redirect
          // the user
          if (authenticationError) {
            const ReturnUrl = encodeURIComponent(window.location.href);
            navigateTo(
              `/sites?authenticationError=${authenticationError}&ReturnUrl=${ReturnUrl}`,
              { type: POST_TYPES.AUTHENTICATION_ERROR },
            );
            // Hang the request (Do not call observer.error, observer.complete, or observer.next)
            // We do this as we're in the middle of navigating and we don't want any failing
            // requests to trigger error pages or otherwise interfere. This leaves the Apollo
            // Link Observable open
            return;
          }
        } catch (err) {
          // If parsing fails, propagate the error
          observer.error(networkError);
          return;
        }
      }
      // For all other errors, propagate as normal
      observer.error(networkError);
    },
    complete: () => {
      observer.complete();
    },
  });

  return () => {
    if (subscription) subscription.unsubscribe();
  };
}));

export default sessionExpiryLink;
